import { useCallback, useMemo } from "react";

import type { GAContextProviderProps } from "@similarweb/ga-connect";
import { useTranslate } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";

import { useToasts } from "app/common/hooks";

import { customRequest } from "../api-client";

const BASE_URL = "/api/v1/google-connections";

const fetchToCustomRequestTransform: GAContextProviderProps["apiConfig"]["customFetch"] = (
    url: string,
    requestOptions,
) =>
    customRequest({
        ...requestOptions,
        body: requestOptions?.body ? JSON.parse(requestOptions.body) : undefined,
        format: "json",
        path: url,
    });

export const useApiConfig = () => {
    const { showErrorToast } = useToasts();
    const { translate } = useTranslate();

    const onError: Required<Required<GAContextProviderProps["apiConfig"]>["handlers"]>["onError"] = useCallback(
        (type, error) => {
            const errorMessage = error instanceof Error ? error.message : String(error);

            Sentry.errorTracker({
                message: "GA Connect request failed",
                e: error,
                level: "error",
                params: {
                    extra: {
                        type,
                    },
                },
            });

            showErrorToast(translate("common.api.error", { error: errorMessage }));
        },
        [showErrorToast, translate],
    );

    const apiConfig: GAContextProviderProps["apiConfig"] = useMemo(
        () => ({
            baseUrl: BASE_URL,
            customFetch: fetchToCustomRequestTransform,
            handlers: {
                onError,
            },
        }),
        [onError],
    );

    return apiConfig;
};

